/**
 * The Posteingang list wire contract: the row, the query parameters, the count response, the
 * German labels, and the SQL fragments that derive channel, status and attachment counts from
 * the communication, task and document tables. The api and the db test execute the same
 * fragments, so the derivation exists once.
 *
 * Routes that carry these types: GET /posteingang (PosteingangListParams in,
 * PosteingangListResponse out) and GET /posteingang/counts (PosteingangCounts out).
 *
 * Rules that are not visible from the code:
 *   - `scanning_center` matches no communication row until intake writes one for a scan; a
 *     filter on it returns an empty page, which is the truthful answer. `upload` no longer
 *     does: an Ablage upload writes its own Eingang (comm_channel 'upload', migration 0132).
 *   - `eingang_status`: stored status of the Eingang (posteingang_status, 0141), set by a
 *     person; drives the Offen/Erledigt filter and the counts. No row = offen.
 *   - `status`: derived from the tasks and the release at read time, shown as "Bearbeitung".
 *     `freigegeben` = every open task staged; `erledigt` also covers a released Eingang.
 *   - `automatisch` counts tasks with routing 'automated' in any status, the population the
 *     Eingang screen labels "Automatisch verarbeitet".
 *   - `unread` and `tasks.freigegeben` are optional on the wire but the api always sends both;
 *     a viewer with no resolvable app_user row sees every row as unread (never claim a mail
 *     was read when the reader is unknown).
 *
 * Example row (valid JSON, keys exactly those of PosteingangItem; a test checks both):
 *
 * ```json
 * {
 *   "id": "6f1c2a0e-1b2c-4d5e-8f90-1234567890ab",
 *   "kanal": "email",
 *   "status": "freigabe_ausstehend",
 *   "eingang_status": "offen",
 *   "tasks": { "total": 2, "offen": 2, "freigabe_ausstehend": 2, "erledigt": 0, "automatisch": 0, "freigegeben": 0 },
 *   "attachment_count": 2,
 *   "attachment_mime_types": ["application/pdf"],
 *   "confidence": "0.920",
 *   "received_at": "2026-09-07T06:14:00.000Z",
 *   "subject": "Schaden Fahrzeug + Werkstattrechnung",
 *   "from": "fam.bauer@bluewin.ch",
 *   "to": "schaden@broker.ch",
 *   "cc": null,
 *   "sync_level_captured": 1,
 *   "task_status": "awaiting_approval",
 *   "task_routing": "freigabe",
 *   "subject_type": "firma",
 *   "subject_id": "0a1b2c3d-4e5f-4a6b-8c7d-0123456789ef",
 *   "kunde_name": "Bauer GmbH",
 *   "your_effective_tier": "full",
 *   "is_private": false,
 *   "can_mark_private": true,
 *   "restricted_to_team": null,
 *   "unread": true,
 *   "unconfirmed": null
 * }
 * ```
 */

import type { ActivityEntry, CommVisibilityTier } from './task';
import type { EmailSyncLevel } from './berechtigungen';

// -- The task kind every Posteingang aggregate excludes ------------------------------
/**
 * task.meta->>'kind' of the Freigabe access-request Pendenz. It shares the mail's comm_id but
 * is not a mail state. The api re-exports it from comm-access.constants.ts so both sides use
 * one literal.
 */
export const ACCESS_REQUEST_TASK_KIND = 'comm_access_request' as const;

/**
 * The predicate that keeps that Pendenz out of a per-Eingang task view. Expects the task
 * table aliased `t`.
 *
 * COALESCE is required, not defensive: task.meta is nullable (0001_spine.sql) and a bare
 * `<>` is NULL for NULL meta, which would drop every ordinary task instead of the one.
 */
export const NON_ACCESS_REQUEST_TASK_SQL =
  `COALESCE(t.meta->>'kind', '') <> '${ACCESS_REQUEST_TASK_KIND}'`;

// -- Kanal ----------------------------------------------------------------------------
export const POSTEINGANG_KANAL = {
  EMAIL: 'email',
  SCANNING_CENTER: 'scanning_center',
  UPLOAD: 'upload',
  PORTAL: 'portal',
} as const;
export type PosteingangKanal = (typeof POSTEINGANG_KANAL)[keyof typeof POSTEINGANG_KANAL];
export const POSTEINGANG_KANAL_VALUES: readonly PosteingangKanal[] = [
  POSTEINGANG_KANAL.EMAIL,
  POSTEINGANG_KANAL.SCANNING_CENTER,
  POSTEINGANG_KANAL.UPLOAD,
  POSTEINGANG_KANAL.PORTAL,
];
export const POSTEINGANG_KANAL_LABELS: Record<PosteingangKanal, string> = {
  [POSTEINGANG_KANAL.EMAIL]: 'E-Mail',
  [POSTEINGANG_KANAL.SCANNING_CENTER]: 'Scanning-Center',
  [POSTEINGANG_KANAL.UPLOAD]: 'Upload',
  [POSTEINGANG_KANAL.PORTAL]: 'Kundenportal',
};

// -- Status of the Eingang as a whole (stored, posteingang_status) + the tab filter ---
export const POSTEINGANG_STATE = { OFFEN: 'offen', ERLEDIGT: 'erledigt' } as const;
export type PosteingangState = (typeof POSTEINGANG_STATE)[keyof typeof POSTEINGANG_STATE];
export const POSTEINGANG_STATE_VALUES: readonly PosteingangState[] = [
  POSTEINGANG_STATE.OFFEN,
  POSTEINGANG_STATE.ERLEDIGT,
];
export const POSTEINGANG_STATE_LABELS: Record<PosteingangState, string> = {
  [POSTEINGANG_STATE.OFFEN]: 'Offen',
  [POSTEINGANG_STATE.ERLEDIGT]: 'Erledigt',
};

// -- Status per Eingang, derived from the task counts by POSTEINGANG_STATUS_SQL ----------
export const POSTEINGANG_STATUS = {
  OFFEN: 'offen',
  IN_BEARBEITUNG: 'in_bearbeitung',
  FREIGABE_AUSSTEHEND: 'freigabe_ausstehend',
  FREIGEGEBEN: 'freigegeben',
  ERLEDIGT: 'erledigt',
} as const;
export type PosteingangStatus = (typeof POSTEINGANG_STATUS)[keyof typeof POSTEINGANG_STATUS];
export const POSTEINGANG_STATUS_VALUES: readonly PosteingangStatus[] = [
  POSTEINGANG_STATUS.OFFEN,
  POSTEINGANG_STATUS.IN_BEARBEITUNG,
  POSTEINGANG_STATUS.FREIGABE_AUSSTEHEND,
  POSTEINGANG_STATUS.FREIGEGEBEN,
  POSTEINGANG_STATUS.ERLEDIGT,
];
export const POSTEINGANG_STATUS_LABELS: Record<PosteingangStatus, string> = {
  [POSTEINGANG_STATUS.OFFEN]: 'Offen',
  [POSTEINGANG_STATUS.IN_BEARBEITUNG]: 'In Bearbeitung',
  [POSTEINGANG_STATUS.FREIGABE_AUSSTEHEND]: 'Freigabe ausstehend',
  [POSTEINGANG_STATUS.FREIGEGEBEN]: 'Freigegeben',
  [POSTEINGANG_STATUS.ERLEDIGT]: 'Erledigt',
};

// -- Wire types ---------------------------------------------------------------------
export interface PosteingangListParams {
  state?: PosteingangState;
  kanal?: PosteingangKanal;
  /** Volltext: sender/recipients, customer, subject, mail text, attachment names (tier-checked). */
  q?: string;
  /** Semantisch: comm ids whose attachments matched (/search?scope=posteingang); tier full only. */
  attachment_hits?: string[];
  archived?: boolean;
  /** true = only rows with no read mark for the calling viewer. */
  unread?: boolean;
  /**
   * P2 hardening 04: true = show ONLY the rows currently HIDDEN for the caller by a
   * sender-hide interval (per-user "privat markiert" + firm "bisherige" rows). Default/false
   * = the normal list, with hidden rows dropped. Mirrors `archived`.
   */
  private_senders?: boolean;
  cursor?: string;
  limit?: number;
}

export interface PosteingangTaskCounts {
  total: number;
  offen: number;
  freigabe_ausstehend: number;
  erledigt: number;
  automatisch: number;
  /** Staged (approved) but not yet released; optional on the wire, always sent. */
  freigegeben?: number;
}

export interface PosteingangItem {
  id: string;
  kanal: PosteingangKanal;
  /** Task-derived processing state (POSTEINGANG_STATUS_SQL), shown as "Bearbeitung". */
  status: PosteingangStatus;
  /** Stored status of the Eingang as a whole, set by a person (posteingang_status). */
  eingang_status: PosteingangState;
  tasks: PosteingangTaskCounts;
  attachment_count: number;
  attachment_mime_types: string[];
  confidence: string | null;
  received_at: string;
  subject: string | null;
  from: string | null;
  to: string | null;
  cc: string | null;
  sync_level_captured: EmailSyncLevel | null;
  task_status: string | null;
  task_routing: string | null;
  subject_type: 'person' | 'firma' | null;
  subject_id: string | null;
  kunde_name: string | null;
  your_effective_tier: CommVisibilityTier;
  is_private: boolean;
  can_mark_private: boolean;
  restricted_to_team: string | null;
  /** No read mark yet for the calling viewer; optional on the wire, always sent. */
  unread?: boolean;
  /** Set only when this row's customer is open unconfirmed (CUSTOMER_UNCONFIRMED_OPEN_SQL), not merely flagged. */
  unconfirmed: { name: string } | null;
}

export interface PosteingangListResponse {
  data: PosteingangItem[];
  page: { next_cursor: string | null; limit: number };
}

export interface PosteingangCounts {
  offen: number;
  /** offen AND unread for the calling viewer. */
  unread: number;
}

/** Body of PATCH /posteingang/:commId/status. */
export interface PosteingangStatusRequest {
  status: PosteingangState;
}

/** Response of PATCH /posteingang/:commId/status. */
export interface PosteingangStatusResponse {
  id: string;
  status: PosteingangState;
  changed_at: string;
}

/** Response of POST /posteingang/:commId/read. */
export interface PosteingangReadResponse {
  id: string;
  read_at: string;
}

// -- Per-user "Absender fuer mich privat markieren" (P2 hardening 04) -----------------------
//
// STRICTLY separate from the per-mail communication.is_private (firm-wide hard exclusion):
// this hides a SENDER's mail from only THIS viewer's Posteingang; the mail stays reachable in
// /kommunikation + search. The firm-wide admin block stays on email_blacklist_rules.

/** One active sender-private row of the caller (GET /posteingang/sender-private). */
export interface SenderPrivateDto {
  id: string;
  /** The bare, lower-cased sender address the viewer marked private. */
  address: string;
  scope: 'user';
  /** NULL = "alle bisherigen" (every past mail hidden too). */
  hide_from: string | null;
  /** NULL = open (still hiding new mail); set = a closed-from-now interval. */
  hide_until: string | null;
  /** true when the interval is still open (hide_until IS NULL) = "right now private". */
  open: boolean;
  marked_at: string;
}

/** GET /posteingang/sender-private — the caller's active rows, newest first. */
export interface SenderPrivateListResponse {
  items: SenderPrivateDto[];
}

/** GET /posteingang/sender-private/preview?address= — the mark/un-mark dialog's state. */
export interface SenderPrivatePreviewResponse {
  address: string;
  /** true when an open active user row already hides this sender for the caller. */
  is_private: boolean;
  /** The open row's id when is_private, else null (the un-mark target). */
  open_row_id: string | null;
  /** Upper-bound count of past inbound mail from the address (labelled "bis zu N" in the UI). */
  past_count: number;
  oldest_received_at: string | null;
  /** A platform/tenant blocklist rule already covers the address (firm-wide block). */
  firm_blocked: boolean;
  /** A tenant-scope hide interval already hides past mail from the address firm-wide. */
  firm_hidden_past: boolean;
  /** The sender is on one of the tenant's own domains (internal-sender warning). */
  is_internal_sender: boolean;
}

/** POST /posteingang/sender-private body. */
export interface SenderPrivateMarkRequest {
  address: string;
  /** true = "ab jetzt und alle bisherigen" (hide_from NULL); false = "ab jetzt". */
  include_past: boolean;
}

/** POST /posteingang/sender-private/:id/unmark body. */
export interface SenderPrivateUnmarkRequest {
  /** true = "alle wieder anzeigen" (deactivate); false = "ab jetzt wieder anzeigen" (close hide_until). */
  reveal_past: boolean;
}

/** GET /email-blocklist/preview?pattern= — "bis zu N bisherige E-Mails" for the admin dialog. */
export interface EmailBlocklistPreviewResponse {
  past_count: number;
}

// -- Gesamtfreigabe (intake_release) ---------------------------------------------------

/** Body of POST /posteingang/:commId/release. An empty body is valid (nothing was shown). */
export interface IntakeReleaseRequest {
  attachments_warning_acknowledged?: boolean;
}

/** One intake_release row, projected for the wire. */
export interface IntakeReleaseView {
  id: string;
  comm_id: string;
  /** app_user.id of the releasing human; null for a system release. */
  released_by: string | null;
  released_by_kind: 'human' | 'system' | 'ai';
  released_at: string;
  all_tasks_approved: boolean;
  attachments_warning_shown: boolean;
  attachments_warning_acknowledged: boolean;
}

/** Response of POST /posteingang/:commId/release. */
export interface IntakeReleaseResponse {
  release: IntakeReleaseView;
  /** Task ids the release's executor seam closed. */
  executed: string[];
  sends_queued: number;
}

/** One deactivated attachment reported by the release's attachments warning. */
export interface ReleaseWarningItem {
  task_id: string;
  task_title: string | null;
  document_id: string;
  filename: string;
}

/** One attachment blocking a send because its delivery channel is not yet supported. */
export interface ReleaseChannelItem {
  task_id: string;
  document_id: string;
  filename: string;
  delivery_channel: string;
}

// -- SQL fragments, executed by the api and by the db test. Plain strings with fixed aliases,
// the convention of the SIG_* expressions in control-backlog.ts: $1 = tenant id,
// c = communication, t = task, d = document, pt and pa = the two LATERALs.
// POSTEINGANG_UNREAD_SQL is the one exception: it also binds $2, the viewer's app_user id.
// Every caller of POSTEINGANG_STATUS_SQL must provide BOTH the pt LATERAL (POSTEINGANG_TASK_COUNTS_SQL)
// and a pr LATERAL or scalar subquery exposing `released boolean` from POSTEINGANG_RELEASE_SQL.

/** Kanal from the communication row. Only c.channel; phone falls to email. */
export const POSTEINGANG_KANAL_SQL =
  `CASE c.channel WHEN 'post' THEN 'scanning_center' WHEN 'portal' THEN 'portal' WHEN 'upload' THEN 'upload' ELSE 'email' END`;

/**
 * LATERAL body over task for one communication c; alias it pt. Access-request tasks excluded.
 * `in_bearbeitung` exists in this result for the status derivation only and never reaches the
 * wire - PosteingangTaskCounts deliberately omits it. `freigegeben` counts a staged task
 * (meta.approval.staged = true) that is still open, in progress or awaiting approval; a
 * staged task that reached done or cancelled some other way no longer counts.
 */
export const POSTEINGANG_TASK_COUNTS_SQL = `
  SELECT count(*)::int AS total,
         count(*) FILTER (WHERE t.status IN ('open','in_progress','awaiting_approval'))::int AS offen,
         count(*) FILTER (WHERE t.status = 'in_progress')::int AS in_bearbeitung,
         count(*) FILTER (WHERE t.status = 'awaiting_approval')::int AS freigabe_ausstehend,
         count(*) FILTER (WHERE t.status IN ('done','cancelled'))::int AS erledigt,
         count(*) FILTER (WHERE t.routing = 'automated')::int AS automatisch,
         count(*) FILTER (WHERE t.status IN ('open','in_progress','awaiting_approval')
                             AND COALESCE(t.meta->'approval'->>'staged','false') = 'true')::int AS freigegeben
    FROM task t
   WHERE t.comm_id = c.id
     AND t.tenant_id = $1
     AND ${NON_ACCESS_REQUEST_TASK_SQL}`;

/**
 * The Eingang status over the pt counts and the pr release flag. A release closes the Eingang
 * regardless of task state; otherwise an Eingang whose tasks are all done or cancelled is
 * erledigt, one whose every open task is staged is freigegeben, then the pre-existing rules.
 * Zero tasks is NOT erledigt: it falls through to offen unless a release row exists.
 */
export const POSTEINGANG_STATUS_SQL = `
  CASE WHEN pr.released THEN 'erledigt'
       WHEN pt.total > 0 AND pt.offen = 0 THEN 'erledigt'
       WHEN pt.offen > 0 AND pt.freigegeben = pt.offen THEN 'freigegeben'
       WHEN pt.freigabe_ausstehend > 0 THEN 'freigabe_ausstehend'
       WHEN pt.in_bearbeitung > 0 THEN 'in_bearbeitung'
       ELSE 'offen' END`;

/**
 * Per-viewer unread mark. $1 = tenant id, $2 = the viewer's app_user id; c = communication.
 * The one fragment that binds $2 - it is never added to the $1-only convention check.
 */
export const POSTEINGANG_UNREAD_SQL =
  `NOT EXISTS (SELECT 1 FROM posteingang_read r WHERE r.tenant_id = $1 AND r.comm_id = c.id AND r.user_id = $2)`;

/**
 * POSTEINGANG_SENDER_HIDDEN_SQL (P2 hardening 04): true when communication c is HIDDEN for the
 * viewer by a sender-hide interval (posteingang_sender_hide). $1 = tenant id, $2 = viewer app_user
 * id (may be NULL → only tenant-scope rows apply, exactly like POSTEINGANG_UNREAD_SQL). c = communication.
 *
 * The buildEingangWhere predicate pushes `NOT (…)` by default (hide the row from list + counts +
 * Posteingang search) and the positive form with filters.privateSenders=true (show ONLY the hidden
 * rows). The $2::uuid cast lets a NULL $2 keep a determinable type on real Postgres (the
 * "could not determine data type of parameter" trap).
 *
 * Matching mirrors matchesBlacklistPattern: an address pattern (contains '@') = exact
 * lower(from_local)@from_domain; a domain pattern = from_domain equal or a sub-domain. from_domain
 * is stored lower-cased at ingest; from_local is lower()-ed here. Uploads/scans (from_domain NULL)
 * are never hidden.
 */
export const POSTEINGANG_SENDER_HIDDEN_SQL = `
  EXISTS (
    SELECT 1 FROM posteingang_sender_hide h
     WHERE h.tenant_id = $1
       AND h.is_active
       AND (h.scope = 'tenant' OR h.app_user_id = $2::uuid)
       AND c.from_domain IS NOT NULL
       AND CASE WHEN position('@' IN h.pattern) > 0
                THEN h.pattern = lower(c.from_local) || '@' || c.from_domain
                ELSE c.from_domain = h.pattern OR c.from_domain LIKE '%.' || h.pattern
            END
       AND (h.hide_from  IS NULL OR c.received_at >= h.hide_from)
       AND (h.hide_until IS NULL OR c.received_at <  h.hide_until)
  )`;

/** Stored Eingang status (0141); no row = 'offen'. $1 = tenant id; c = communication. */
export const POSTEINGANG_EINGANG_STATUS_SQL =
  `COALESCE((SELECT ps.status FROM posteingang_status ps WHERE ps.tenant_id = $1 AND ps.comm_id = c.id), 'offen')`;

/** Whether the Eingang already carries a Gesamtfreigabe. $1 = tenant id; c = communication. */
export const POSTEINGANG_RELEASE_SQL =
  `EXISTS (SELECT 1 FROM intake_release ir WHERE ir.tenant_id = $1 AND ir.comm_id = c.id)`;

/**
 * LATERAL body over document for one communication c; alias it pa. No document.state filter,
 * matching the attachment listing of the mail detail, so the list and the detail never disagree.
 *
 * An attachment is a document whose PRIMARY link is the communication (linked_type/linked_id),
 * OR one tied to it by a relation='connected' document_link. The second path keeps an Eingang's
 * file after a broker RE-classifies it (which re-points linked_type to the Police/Kunde): the
 * connected link is written on reclassify and survives it, so the paperclip does not vanish.
 */
export const POSTEINGANG_ATTACHMENTS_SQL = `
  SELECT count(*)::int AS attachment_count,
         COALESCE(array_agg(DISTINCT d.mime_type) FILTER (WHERE d.mime_type IS NOT NULL), '{}') AS attachment_mime_types
    FROM document d
   WHERE d.tenant_id = $1
     AND (
       (d.linked_type = 'communication' AND d.linked_id = c.id)
       OR EXISTS (
         SELECT 1 FROM document_link dl
          WHERE dl.tenant_id = $1
            AND dl.document_id = d.id
            AND dl.object_type = 'communication'
            AND dl.object_id = c.id
       )
     )`;

// -- Activity history of an Eingang + the sent replies in it -----------------------------

/** meta.kind of the send worker's activity; meta also has `communication_id` and `sent_by`. */
export const POSTEINGANG_SENT_ACTIVITY_KIND = 'email_sent' as const;

/** One row of GET /posteingang/:commId/activity: an activity of ANY task of the Eingang. */
export interface PosteingangActivityEntry extends ActivityEntry {
  task_id: string;
  /** Who did it: the releasing person for a sent reply, else the human actor. Null = system. */
  actor_name: string | null;
  /** Set when the entry is a sent reply whose outbound mail exists. */
  sent: { communication_id: string; to: string | null; subject: string | null; attachment_count: number } | null;
}

export interface PosteingangActivityResponse {
  data: PosteingangActivityEntry[];
}

/** GET /posteingang/:commId/sent/:sentId; body only at tier full on the Eingang. */
export interface PosteingangSentMail {
  id: string;
  subject: string | null;
  from: string | null;
  to: string | null;
  cc: string | null;
  sent_at: string;
  sent_by_name: string | null;
  body: string | null;
  body_html: string | null;
  tier: 'full' | 'metadata';
}

/** A uuid test for jsonb text, so a cast never fails on a foreign meta value. */
const UUID_RE = `'^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$'`;

/**
 * History of one Eingang: activities of all its tasks (access requests excluded), oldest
 * first, with actor name and sent-reply recipient/subject. $1 tenant, $2 communication.
 */
export const POSTEINGANG_ACTIVITY_SQL = `SELECT a.id, a.task_id, a.actor_kind, a.actor_id, a.type, a.content, a.meta, a.created_at,
       NULLIF(TRIM(COALESCE(au.display_name, au_sub.display_name, '')), '') AS actor_display_name,
       COALESCE(au.email, au_sub.email) AS actor_email,
       oc.id      AS sent_communication_id,
       oc."to"    AS sent_to,
       oc.subject AS sent_subject,
       -- Attachment count of the SENT reply (documents linked to the OUTBOUND comm oc), so the
       -- history entry can show "· N Anhänge". 0 when there is no outbound mail. Mirrors
       -- POSTEINGANG_ATTACHMENTS_SQL but keyed on oc.id.
       (SELECT count(*)::int FROM document d
         WHERE d.tenant_id = a.tenant_id
           AND (
             (d.linked_type = 'communication' AND d.linked_id = oc.id)
             OR EXISTS (SELECT 1 FROM document_link dl
                         WHERE dl.tenant_id = a.tenant_id AND dl.document_id = d.id
                           AND dl.object_type = 'communication' AND dl.object_id = oc.id)
           )) AS sent_attachment_count
  FROM activity a
  JOIN task t ON t.id = a.task_id AND t.tenant_id = a.tenant_id
  LEFT JOIN app_user au ON au.id = COALESCE(
         CASE WHEN a.meta->>'sent_by' ~* ${UUID_RE} THEN (a.meta->>'sent_by')::uuid END,
         a.actor_id)
  -- task services write the Keycloak sub into actor_id (TaskActor.sub), not app_user.id
  LEFT JOIN app_user au_sub ON au.id IS NULL AND au_sub.keycloak_sub = a.actor_id::text
  LEFT JOIN communication oc
         ON oc.id = CASE WHEN a.meta->>'communication_id' ~* ${UUID_RE}
                         THEN (a.meta->>'communication_id')::uuid END
        AND oc.tenant_id = a.tenant_id
        AND oc.direction = 'outbound'
 WHERE a.tenant_id = $1
   AND t.comm_id = $2
   AND ${NON_ACCESS_REQUEST_TASK_SQL}
 ORDER BY a.created_at ASC, a.id ASC`;

/** One reply of Eingang $2, only if an activity of that Eingang links it. $1 tenant, $3 reply. */
export const POSTEINGANG_SENT_MAIL_SQL = `SELECT oc.id, oc.subject, oc."from" AS from, oc."to" AS to, oc.cc,
       oc.received_at AS sent_at, oc.body, oc.body_html,
       NULLIF(TRIM(COALESCE(au.display_name, '')), '') AS sent_by_display_name,
       au.email AS sent_by_email
  FROM activity a
  JOIN task t ON t.id = a.task_id AND t.tenant_id = a.tenant_id
  JOIN communication oc
    ON oc.id = $3::uuid
   AND oc.tenant_id = a.tenant_id
   AND oc.direction = 'outbound'
  LEFT JOIN app_user au ON au.id =
       CASE WHEN a.meta->>'sent_by' ~* ${UUID_RE} THEN (a.meta->>'sent_by')::uuid END
 WHERE a.tenant_id = $1
   AND t.comm_id = $2
   AND a.meta->>'communication_id' = oc.id::text
 ORDER BY a.created_at DESC
 LIMIT 1`;

// -- Search in the Posteingang -------------------------------------------------------------

/** Escape a user string for ILIKE ... ESCAPE '\\': %, _ and the backslash become literal. */
export function escapeLikePattern(q: string): string {
  return q.replace(/[\\%_]/g, (ch) => `\\${ch}`);
}

/**
 * Volltext fragments over communication c; `p` is the ILIKE parameter, $1 = tenant id.
 * Split by what a match reveals: meta (every tier), subject (full, title), content (full).
 */
export function posteingangSearchSql(p: string): { meta: string; subject: string; content: string } {
  const like = (col: string) => `${col} ILIKE ${p} ESCAPE '\\'`;
  return {
    meta: `(${like('c."from"')} OR ${like('c."to"')} OR ${like('c.cc')}
      OR EXISTS (
        SELECT 1 FROM comm_reference cr
          LEFT JOIN firma fs  ON cr.subject_type = 'firma'  AND fs.id = cr.subject_id AND fs.tenant_id = $1
          LEFT JOIN person ps ON cr.subject_type = 'person' AND ps.id = cr.subject_id AND ps.tenant_id = $1
         WHERE cr.comm_id = c.id AND cr.tenant_id = $1
           AND (${like('fs.name')}
                OR ${like(`TRIM(COALESCE(ps.first_name, '') || ' ' || COALESCE(ps.last_name, ''))`)}
                OR ${like(`TRIM(COALESCE(ps.last_name, '') || ' ' || COALESCE(ps.first_name, ''))`)})))`,
    subject: `(${like('c.subject')})`,
    content: `(${like('c.body')}
      OR EXISTS (
        SELECT 1 FROM document d
         WHERE d.tenant_id = $1
           AND ${like('d.filename')}
           AND (
             (d.linked_type = 'communication' AND d.linked_id = c.id)
             OR EXISTS (
               SELECT 1 FROM document_link dl
                WHERE dl.tenant_id = $1 AND dl.document_id = d.id
                  AND dl.object_type = 'communication' AND dl.object_id = c.id
             )
           )))`,
  };
}

/**
 * Semantisch candidates: nearest chunks of active, unique documents linked to a mail
 * (directly or via document_link). $1 tenant, $2 vector; `limit` is a trusted constant.
 */
export function posteingangSemanticChunksSql(limit: number): string {
  return `SELECT dc.document_id,
       d.filename,
       'communication'::text AS linked_type,
       COALESCE(CASE WHEN d.linked_type = 'communication' THEN d.linked_id END, dl.object_id) AS linked_id,
       dc.content,
       (dc.embedding <=> $2::vector) AS distance
  FROM document_chunk dc
  JOIN document d ON d.id = dc.document_id AND d.tenant_id = $1
  LEFT JOIN document_link dl
    ON dl.tenant_id = $1 AND dl.document_id = d.id AND dl.object_type = 'communication'
 WHERE dc.tenant_id = $1
   AND dc.embedding IS NOT NULL
   AND d.dedup_status = 'unique'
   AND d.state = 'active'
   AND (d.linked_type = 'communication' OR dl.object_id IS NOT NULL)
 ORDER BY dc.embedding <=> $2::vector
 LIMIT ${Math.trunc(limit)}`;
}
